import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  CreditCard,
  FileQuestion,
  FileText,
  Headphones,
  HelpCircle,
  Laptop,
  LifeBuoy,
  Lock,
  Mail,
  MessageCircle,
  Phone,
  Search,
  Send,
  ShieldCheck,
  Smartphone,
  Video,
} from "lucide-react";

type SupportCategory =
  | "All"
  | "Account"
  | "Live Classes"
  | "Tests"
  | "Study Notes"
  | "Payments"
  | "Technical";

type FAQ = {
  id: number;
  question: string;
  answer: string;
  category: Exclude<SupportCategory, "All">;
  keywords: string;
};

type SupportCard = {
  title: string;
  desc: string;
  category: Exclude<SupportCategory, "All">;
  responseTime: string;
  icon: React.ReactNode;
  keywords: string;
};

type ContactChannel = {
  title: string;
  desc: string;
  action: string;
  time: string;
  icon: React.ReactNode;
};

type SupportFlow = {
  step: string;
  title: string;
  desc: string;
};

const categories: SupportCategory[] = [
  "All",
  "Account",
  "Live Classes",
  "Tests",
  "Study Notes",
  "Payments",
  "Technical",
];

const supportCards: SupportCard[] = [
  {
    title: "Account and Login Help",
    desc: "Get help with student login, OTP verification, password access, profile details and student dashboard access.",
    category: "Account",
    responseTime: "Usually resolved in 10 minutes",
    icon: <Lock size={24} />,
    keywords:
      "student login help, OTP issue, account access, student dashboard login, registration help, profile update",
  },
  {
    title: "Live Class Support",
    desc: "Get priority help for joining live classes, audio issues, video problems, browser permissions and class schedule confusion.",
    category: "Live Classes",
    responseTime: "Priority support during class hours",
    icon: <Video size={24} />,
    keywords:
      "live class support, online class issue, video not working, audio problem, class schedule, live batch help",
  },
  {
    title: "Test and Quiz Support",
    desc: "Report test submission issues, result errors, quiz access problems, score mismatch and ranking-related questions.",
    category: "Tests",
    responseTime: "Resolved within 24 hours",
    icon: <FileQuestion size={24} />,
    keywords:
      "test support, quiz issue, mock test result, online test error, score report, ranking issue, test submission problem",
  },
  {
    title: "Study Notes Support",
    desc: "Get help with PDF notes, chapter downloads, missing study material, DPPs, assignments and revision resources.",
    category: "Study Notes",
    responseTime: "Usually resolved same day",
    icon: <BookOpen size={24} />,
    keywords:
      "study notes support, PDF notes, chapter notes, assignments, DPP, revision material, study resources",
  },
  {
    title: "Payment and Course Access",
    desc: "Get support for fee payment, course activation, invoice, payment receipt, failed transaction, refund request and plan upgrade.",
    category: "Payments",
    responseTime: "Support within 24 hours",
    icon: <CreditCard size={24} />,
    keywords:
      "payment support, course activation, invoice request, refund request, payment receipt, failed transaction, course access",
  },
  {
    title: "Technical Support",
    desc: "Get step-by-step help for website loading, app issues, video buffering, browser problems, device compatibility and dashboard errors.",
    category: "Technical",
    responseTime: "Step-by-step guidance",
    icon: <Laptop size={24} />,
    keywords:
      "technical support, website loading issue, app issue, browser problem, device compatibility, video buffering, dashboard error",
  },
];

const faqs: FAQ[] = [
  {
    id: 1,
    category: "Account",
    question: "I am unable to login to my student account. What should I do?",
    answer:
      "First check that you are using the correct registered mobile number or email address. If the OTP is not arriving, wait for a few minutes and try again. If the issue continues, create a support ticket with your registered details and a screenshot.",
    keywords:
      "student login issue, OTP not coming, login problem, registered mobile number, student account access",
  },
  {
    id: 2,
    category: "Live Classes",
    question: "How can I join a live class?",
    answer:
      "Open your student dashboard and go to the Live Classes or enrolled course section. Select the scheduled class and click the Join Live Class button. If the class is not visible, check whether your course is active and your batch schedule has been assigned.",
    keywords:
      "join live class, live class link, student dashboard, enrolled course, batch schedule",
  },
  {
    id: 3,
    category: "Live Classes",
    question: "What should I do if live class video or audio is not working?",
    answer:
      "Refresh the page, check your internet connection, allow browser audio and video permissions, and try using the latest version of Google Chrome. If the issue still continues, contact support with a screenshot, device name and browser details.",
    keywords:
      "live class audio issue, live class video issue, browser permission, Chrome browser, online class problem",
  },
  {
    id: 4,
    category: "Tests",
    question: "My test was submitted but the result is not showing. What should I do?",
    answer:
      "Sometimes test result processing may take a few minutes. Refresh your dashboard after some time. If the result is still missing, share the test name, test date, screenshot and registered account details with support.",
    keywords:
      "test result not showing, online test submitted, mock test result, score report issue",
  },
  {
    id: 5,
    category: "Tests",
    question: "Can I reattempt a quiz or test?",
    answer:
      "Daily quizzes may allow multiple attempts depending on course settings. Full mock tests are usually locked after submission to maintain ranking accuracy, but practice tests may be available again for revision.",
    keywords:
      "reattempt quiz, reattempt test, mock test rules, daily quiz, practice test",
  },
  {
    id: 6,
    category: "Study Notes",
    question: "Where can I download study notes and PDFs?",
    answer:
      "Open your course dashboard and go to the Study Notes or Resources section. Notes are usually organized by subject, chapter and course. You can view them online or download them if download access is enabled for your course.",
    keywords:
      "download study notes, PDF notes, course dashboard, study resources, chapter notes",
  },
  {
    id: 7,
    category: "Payments",
    question: "I paid for a course but it is not active yet. What should I do?",
    answer:
      "Please wait for a few minutes after payment. If the course is still not active, share your payment screenshot, transaction ID, registered mobile number and course name with the support team for verification.",
    keywords:
      "course not active after payment, payment screenshot, transaction ID, course activation, payment verification",
  },
  {
    id: 8,
    category: "Technical",
    question: "Which browser is best for using Mediit Academy?",
    answer:
      "For the best experience, use the latest version of Google Chrome. Keep your browser updated and use a stable internet connection, especially during live classes and online tests.",
    keywords:
      "best browser for online classes, Google Chrome, technical requirement, online learning browser",
  },
  {
    id: 9,
    category: "Technical",
    question: "The website is loading slowly. How can I fix it?",
    answer:
      "Try clearing browser cache, closing extra tabs, switching to a stronger internet connection and refreshing the page. If the issue continues, share your device name, browser details and screenshot with support.",
    keywords:
      "website loading slowly, clear browser cache, slow dashboard, internet issue, technical support",
  },
  {
    id: 10,
    category: "Payments",
    question: "How can I request an invoice or payment receipt?",
    answer:
      "You can request an invoice from the Payments section of your dashboard. If it is not available there, create a support ticket with your payment details and registered account information.",
    keywords:
      "invoice request, payment receipt, payment details, course invoice, fee receipt",
  },
];

const quickActions = [
  "Reset login access",
  "Report live class issue",
  "Ask about course access",
  "Download study notes",
  "Check payment status",
  "Report test problem",
];

const contactChannels: ContactChannel[] = [
  {
    title: "Support Ticket",
    desc: "Best for account, payment, course access, test, notes and technical issues.",
    action: "Create Ticket",
    time: "Response within 24 hours",
    icon: <LifeBuoy size={24} />,
  },
  {
    title: "Live Class Help",
    desc: "Use this during live classes for joining, audio, video or schedule issues.",
    action: "Get Class Help",
    time: "Priority during class hours",
    icon: <Video size={24} />,
  },
  {
    title: "WhatsApp Support",
    desc: "Quick help for common student questions, updates and support guidance.",
    action: "Chat on WhatsApp",
    time: "Available during support hours",
    icon: <MessageCircle size={24} />,
  },
];

const supportFlow: SupportFlow[] = [
  {
    step: "01",
    title: "Search Your Issue",
    desc: "Use the support search or FAQ section to find quick answers for login, class, test, notes, payment or technical issues.",
  },
  {
    step: "02",
    title: "Choose Category",
    desc: "Select the correct category so your issue reaches the right support team faster.",
  },
  {
    step: "03",
    title: "Submit Details",
    desc: "Share your registered mobile number, email, course name, screenshot and clear issue description.",
  },
  {
    step: "04",
    title: "Get Resolution",
    desc: "The support team reviews your request and guides you with the next step based on your account and course details.",
  },
];

const seoKeywords = [
  "Mediit Academy student support",
  "student support center",
  "online course support",
  "student login help",
  "OTP login issue",
  "live class support",
  "online class audio video issue",
  "course access help",
  "study notes support",
  "PDF notes download",
  "test series support",
  "mock test result issue",
  "payment support",
  "refund request support",
  "invoice request",
  "technical support",
  "student dashboard help",
  "CBSE online class support",
  "JEE course support",
  "NEET course support",
  "LMS support",
].join(", ");

const StudentSupport: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<SupportCategory>("All");
  const [openFAQ, setOpenFAQ] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const title =
      "Student Support | Login, Live Classes, Tests, Notes and Payment Help - Mediit Academy";

    const description =
      "Get Mediit Academy student support for login, OTP, live classes, course access, study notes, test series, payments, refunds, invoices, technical issues and student dashboard help.";

    document.title = title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setPropertyTag = (property: string, content: string) => {
      let tag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag("description", description);
    setMetaTag("keywords", seoKeywords);
    setMetaTag("robots", "index, follow");
    setMetaTag("author", "Mediit Academy");

    setPropertyTag("og:title", title);
    setPropertyTag("og:description", description);
    setPropertyTag("og:type", "website");
    setPropertyTag("og:site_name", "Mediit Academy");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);

    let canonical = document.querySelector(
      `link[rel="canonical"]`
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", `${window.location.origin}/support`);
  }, []);

  const filteredCards = useMemo(() => {
    if (activeCategory === "All") return supportCards;
    return supportCards.filter((card) => card.category === activeCategory);
  }, [activeCategory]);

  const filteredFAQs = useMemo(() => {
    const byCategory =
      activeCategory === "All"
        ? faqs
        : faqs.filter((faq) => faq.category === activeCategory);

    if (!searchQuery.trim()) return byCategory;

    const query = searchQuery.toLowerCase();

    return byCategory.filter(
      (faq) =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.category.toLowerCase().includes(query) ||
        faq.keywords.toLowerCase().includes(query)
    );
  }, [activeCategory, searchQuery]);

  const supportSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Mediit Academy Student Support",
    description:
      "Student support page for login help, live class issues, course access, study notes, tests, payments, refunds and technical support.",
    url: "/support",
    keywords: seoKeywords,
    mainEntity: supportCards.map((card) => ({
      "@type": "Service",
      name: card.title,
      description: card.desc,
      serviceType: card.category,
      keywords: card.keywords,
    })),
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  const goToDashboard = () => {
    window.location.href = "/student-dashboard";
  };

  const goToTicket = () => {
    window.location.href = "/support-ticket";
  };

  const scrollToFAQ = () => {
    const section = document.getElementById("faq-section");
    section?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#f6f8fc] font-['Inter'] text-[#101827]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(supportSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema),
        }}
      />

      <main>
        {/* HERO */}
        <section
          className="relative overflow-hidden bg-[#f6f8fc]"
          aria-labelledby="student-support-heading"
        >
          <div className="absolute -left-24 top-0 h-96 w-96 rounded-full bg-blue-200/50 blur-3xl" />
          <div className="absolute right-0 top-10 h-96 w-96 rounded-full bg-orange-100 blur-3xl" />
          <div className="absolute bottom-0 left-[45%] h-72 w-72 rounded-full bg-green-100 blur-3xl" />

          <div className="relative mx-auto max-w-[1380px] px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 shadow-sm">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-black text-white">
                    ?
                  </span>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                    Student Support Center
                  </p>
                </div>

                <h1
                  id="student-support-heading"
                  className="mt-6 max-w-4xl text-4xl font-black leading-tight tracking-[-0.05em] text-slate-950 sm:text-5xl lg:text-[64px]"
                >
                  Get Help for Login, Live Classes, Tests, Notes and Payments
                </h1>

                <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                  Search FAQs, choose your support category or create a support
                  ticket for student login, OTP issues, live class access,
                  online tests, study notes, course activation, payments,
                  refunds, invoices and technical problems.
                </p>

                <p className="sr-only">{seoKeywords}</p>

                <div className="mt-8 max-w-2xl rounded-[26px] border border-slate-200 bg-white p-3 shadow-xl shadow-slate-900/5">
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <div className="relative flex-1">
                      <Search
                        size={18}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                        aria-hidden="true"
                      />
                      <input
                        value={searchQuery}
                        onChange={(event) =>
                          setSearchQuery(event.target.value)
                        }
                        placeholder="Search issue: OTP, live class, payment, test..."
                        aria-label="Search support FAQs"
                        className="min-h-[54px] w-full rounded-2xl border border-slate-100 bg-[#f8fbff] pl-11 pr-5 text-sm font-semibold text-slate-800 outline-none transition-all focus:border-blue-300 focus:bg-white"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={scrollToFAQ}
                      className="rounded-2xl bg-[#101827] px-7 py-4 text-sm font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-slate-800"
                    >
                      Search Help
                    </button>
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={goToTicket}
                    aria-label="Create student support ticket"
                    className="rounded-2xl bg-blue-600 px-7 py-4 text-sm font-black text-white shadow-xl shadow-blue-600/20 transition-all duration-300 hover:-translate-y-1 hover:bg-blue-700"
                  >
                    Create Support Ticket
                  </button>

                  <button
                    type="button"
                    onClick={goToDashboard}
                    aria-label="Open student dashboard"
                    className="rounded-2xl border border-slate-200 bg-white px-7 py-4 text-sm font-black text-slate-950 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-300"
                  >
                    Open Student Dashboard
                  </button>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="relative mx-auto max-w-[590px]">
                  <div className="rounded-[36px] border border-slate-100 bg-white p-5 shadow-[0_30px_90px_rgba(15,23,42,0.12)]">
                    <div className="rounded-[30px] bg-[#101827] p-6 text-white">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-200">
                            Help Center
                          </p>
                          <h2 className="mt-2 text-2xl font-black">
                            How can we help you today?
                          </h2>
                        </div>

                        <span className="w-fit rounded-full bg-green-400/10 px-4 py-2 text-xs font-black text-green-300">
                          Support Active
                        </span>
                      </div>

                      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {quickActions.map((item) => (
                          <button
                            key={item}
                            type="button"
                            onClick={goToTicket}
                            aria-label={`Create ticket for ${item}`}
                            className="rounded-2xl border border-white/10 bg-white/10 p-4 text-left text-sm font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:text-slate-950"
                          >
                            {item}
                          </button>
                        ))}
                      </div>

                      <div className="mt-6 rounded-3xl bg-white p-5 text-slate-950">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <p className="text-sm font-black">
                              Average Response Time
                            </p>
                            <p className="mt-1 text-xs font-bold text-slate-500">
                              Account, class and payment related support
                            </p>
                          </div>

                          <div className="rounded-2xl bg-blue-50 px-4 py-3 text-center">
                            <p className="text-2xl font-black text-blue-600">
                              24h
                            </p>
                            <p className="text-[11px] font-bold text-slate-500">
                              Max
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="absolute -bottom-6 left-8 right-8 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-xl sm:block">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-black text-slate-950">
                          Support Hours
                        </p>
                        <p className="mt-1 text-xs font-bold text-slate-500">
                          Monday to Saturday · 09:00 AM to 07:00 PM
                        </p>
                      </div>

                      <span className="rounded-xl bg-green-50 px-4 py-2 text-xs font-black text-green-600">
                        Online
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT CHANNELS */}
        <section className="bg-white py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-8 grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-6">
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Contact Support
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Choose the Best Way to Get Student Support
                </h2>
              </div>

              <p className="text-sm leading-6 text-slate-500 lg:col-span-6">
                Students can raise tickets, request help during live class,
                contact support for account and payment issues or use WhatsApp
                support for quick guidance.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              {contactChannels.map((channel) => (
                <article
                  key={channel.title}
                  className="group rounded-[30px] border border-slate-100 bg-[#f8fbff] p-6 transition-all duration-300 hover:-translate-y-2 hover:bg-white hover:shadow-xl"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-blue-600 shadow-sm transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                    {channel.icon}
                  </div>

                  <h3 className="text-2xl font-black text-slate-950">
                    {channel.title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {channel.desc}
                  </p>

                  <div className="mt-5 rounded-2xl bg-white p-4">
                    <p className="text-xs font-bold text-slate-500">
                      Response
                    </p>
                    <p className="mt-1 text-sm font-black text-slate-950">
                      {channel.time}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={goToTicket}
                    className="mt-6 w-full rounded-2xl bg-[#101827] px-5 py-3 text-sm font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-slate-800"
                  >
                    {channel.action}
                  </button>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* SUPPORT CATEGORIES */}
        <section className="bg-[#f6f8fc] py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Support Topics
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Select Your Issue Category
                </h2>
              </div>

              <p className="max-w-xl text-sm leading-6 text-slate-500">
                Support is organized by common student needs so every issue can
                reach the right support topic faster.
              </p>
            </div>

            <div
              className="mb-8 flex gap-3 overflow-x-auto pb-2"
              aria-label="Support category filters"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  aria-label={`Show ${category} support topics`}
                  className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-black transition-all duration-300 ${
                    activeCategory === category
                      ? "border-[#101827] bg-[#101827] text-white shadow-lg shadow-slate-900/15"
                      : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:border-blue-300 hover:text-blue-600"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filteredCards.map((card) => (
                <article
                  key={card.title}
                  title={card.keywords}
                  className="group rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-blue-200 hover:shadow-[0_22px_60px_rgba(15,23,42,0.12)]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                      {card.icon}
                    </div>

                    <span className="rounded-full border border-slate-100 bg-slate-50 px-3 py-1 text-xs font-black text-slate-600">
                      {card.category}
                    </span>
                  </div>

                  <h3 className="mt-5 text-2xl font-black tracking-[-0.03em] text-slate-950">
                    {card.title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {card.desc}
                  </p>

                  <p className="sr-only">{card.keywords}</p>

                  <div className="mt-5 rounded-2xl bg-blue-50 p-4">
                    <p className="text-xs font-bold text-blue-600">
                      Support Time
                    </p>
                    <p className="mt-1 text-sm font-black text-slate-950">
                      {card.responseTime}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={goToTicket}
                    className="mt-6 w-full rounded-2xl bg-[#101827] px-5 py-3 text-sm font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-slate-800"
                  >
                    Ask Support
                  </button>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq-section" className="bg-white py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <div className="sticky top-6 rounded-[34px] bg-[#101827] p-7 text-white shadow-2xl lg:p-8">
                  <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-200">
                    FAQ Center
                  </p>

                  <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] sm:text-4xl">
                    Common Student Questions Answered Clearly
                  </h2>

                  <p className="mt-4 text-sm leading-7 text-white/70">
                    Search FAQs or select a category to find answers related to
                    login, classes, tests, notes, payments and technical issues.
                  </p>

                  <div className="mt-6 rounded-2xl bg-white/10 p-4">
                    <p className="text-xs font-bold text-white/50">
                      Showing FAQs
                    </p>
                    <p className="mt-1 text-2xl font-black">
                      {filteredFAQs.length}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={goToTicket}
                    className="mt-6 w-full rounded-2xl bg-white px-5 py-4 text-sm font-black text-slate-950 transition-all duration-300 hover:-translate-y-1"
                  >
                    Still Need Help?
                  </button>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="space-y-4">
                  {filteredFAQs.length > 0 ? (
                    filteredFAQs.map((faq) => {
                      const isOpen = openFAQ === faq.id;

                      return (
                        <article
                          key={faq.id}
                          className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:border-blue-200 hover:shadow-lg"
                        >
                          <button
                            type="button"
                            onClick={() => setOpenFAQ(isOpen ? 0 : faq.id)}
                            aria-expanded={isOpen}
                            className="flex w-full items-start justify-between gap-5 p-5 text-left"
                          >
                            <div>
                              <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-600">
                                {faq.category}
                              </span>

                              <h3 className="mt-3 text-lg font-black leading-7 text-slate-950">
                                {faq.question}
                              </h3>
                            </div>

                            <span
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-xl font-black transition-all duration-300 ${
                                isOpen
                                  ? "rotate-45 bg-blue-600 text-white"
                                  : "bg-slate-50 text-slate-950"
                              }`}
                            >
                              +
                            </span>
                          </button>

                          <div
                            className={`grid transition-all duration-300 ${
                              isOpen
                                ? "grid-rows-[1fr] opacity-100"
                                : "grid-rows-[0fr] opacity-0"
                            }`}
                          >
                            <div className="overflow-hidden">
                              <p className="border-t border-slate-100 px-5 pb-5 pt-4 text-sm leading-7 text-slate-500">
                                {faq.answer}
                              </p>
                              <p className="sr-only">{faq.keywords}</p>
                            </div>
                          </div>
                        </article>
                      );
                    })
                  ) : (
                    <div className="rounded-[28px] border border-slate-200 bg-[#f8fbff] p-8 text-center">
                      <h3 className="text-2xl font-black text-slate-950">
                        No FAQ Found
                      </h3>
                      <p className="mt-3 text-sm leading-6 text-slate-500">
                        Try a different keyword or create a support ticket for
                        personalized help.
                      </p>
                      <button
                        type="button"
                        onClick={goToTicket}
                        className="mt-6 rounded-2xl bg-[#101827] px-6 py-3 text-sm font-black text-white"
                      >
                        Create Ticket
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SUPPORT FLOW */}
        <section className="bg-[#f6f8fc] py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-9 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  How Support Works
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Simple Process to Solve Student Issues
                </h2>
              </div>

              <p className="max-w-xl text-sm leading-6 text-slate-500">
                Clear details help the support team resolve issues faster.
                Always include registered mobile number, course name, screenshot
                and error message if available.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {supportFlow.map((item) => (
                <article
                  key={item.step}
                  className="rounded-[28px] border border-slate-100 bg-white p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#101827] text-lg font-black text-white">
                    {item.step}
                  </div>

                  <h3 className="text-xl font-black text-slate-950">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {item.desc}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* TICKET FORM PREVIEW */}
        <section className="bg-white py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-5">
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Submit Your Question
                </p>

                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Students Can Submit Their Support Question Directly
                </h2>

                <p className="mt-4 text-sm leading-7 text-slate-500">
                  This form UI can be connected with your backend later. It is
                  useful for creating support tickets from the LMS dashboard,
                  student profile or support page.
                </p>

                <div className="mt-6 rounded-[26px] bg-[#f8fbff] p-5">
                  <p className="text-sm font-black text-slate-950">
                    Recommended Details
                  </p>

                  <div className="mt-4 space-y-3">
                    {[
                      "Registered mobile number or email",
                      "Course name and class",
                      "Screenshot or error message",
                      "Clear description of the issue",
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-3">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-green-50 text-xs font-black text-green-600">
                          ✓
                        </span>
                        <p className="text-sm font-bold text-slate-600">
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="rounded-[34px] border border-slate-200 bg-[#f8fbff] p-5 shadow-sm lg:p-6">
                  <div className="rounded-[28px] bg-white p-5">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                          Full Name
                        </label>
                        <input
                          placeholder="Enter student name"
                          aria-label="Enter student full name"
                          className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold outline-none focus:border-blue-300"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                          Mobile / Email
                        </label>
                        <input
                          placeholder="Registered contact"
                          aria-label="Enter registered mobile number or email"
                          className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold outline-none focus:border-blue-300"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                          Category
                        </label>
                        <select
                          aria-label="Select support category"
                          className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold outline-none focus:border-blue-300"
                        >
                          <option>Account</option>
                          <option>Live Classes</option>
                          <option>Tests</option>
                          <option>Study Notes</option>
                          <option>Payments</option>
                          <option>Technical</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                          Priority
                        </label>
                        <select
                          aria-label="Select support priority"
                          className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold outline-none focus:border-blue-300"
                        >
                          <option>Normal</option>
                          <option>High</option>
                          <option>Urgent</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                        Your Question
                      </label>
                      <textarea
                        placeholder="Write your issue in detail..."
                        rows={5}
                        aria-label="Write support question"
                        className="mt-2 w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm font-semibold outline-none focus:border-blue-300"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={goToTicket}
                      className="mt-5 w-full rounded-2xl bg-blue-600 px-5 py-4 text-sm font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-blue-700"
                    >
                      Submit Support Request
                    </button>

                    <p className="mt-3 text-center text-xs font-semibold text-slate-500">
                      This form UI is ready. You can connect it with Spring Boot
                      or Node.js backend later.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#f6f8fc] py-12">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-[36px] bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 p-8 text-white shadow-2xl lg:p-10">
              <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-blue-500/30 blur-3xl" />
              <div className="absolute -bottom-10 left-10 h-44 w-44 rounded-full bg-orange-500/20 blur-3xl" />

              <div className="relative flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-200">
                    We Are Here to Help
                  </p>

                  <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] sm:text-4xl">
                    Student Support for Every Learning Step
                  </h2>

                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/70">
                    From login and live classes to tests, notes, payments and
                    technical issues, Mediit Academy support helps students
                    continue learning without confusion.
                  </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={goToTicket}
                    className="rounded-2xl bg-white px-7 py-4 text-sm font-black text-slate-950 transition-all duration-300 hover:-translate-y-1"
                  >
                    Create Ticket
                  </button>

                  <button
                    type="button"
                    onClick={scrollToFAQ}
                    className="rounded-2xl border border-white/15 bg-white/10 px-7 py-4 text-sm font-black text-white backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:bg-white/15"
                  >
                    View FAQs
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL TRUST NOTE */}
        <section className="bg-white px-4 py-12 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-[1380px] rounded-[2rem] border border-slate-200 bg-slate-50 p-6 sm:p-8">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                <ShieldCheck size={24} aria-hidden="true" />
              </div>

              <div>
                <h3 className="text-xl font-black text-slate-950">
                  Safe Support Reminder
                </h3>
                <p className="mt-2 text-sm font-medium leading-7 text-slate-600">
                  Students should never share OTP, password or sensitive payment
                  details with unknown persons. Always use the official Mediit
                  Academy support channels for account, course, payment or
                  technical help.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default StudentSupport;